//@flow
import React, {useState, useRef, useMemo} from 'react';
import {
  Grid,
  Hidden,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';

import SearchInput from '../../../../elements/SearchInput';
import CalendarIcon from '../../../../elements/icons/CalendarIcon';
import {colors} from '../../../../assets/styles/colors';
import {Close} from '@material-ui/icons';

import type {Theme} from '@material-ui/core';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import DatePicker from '../../../../elements/DatePicker/DatePicker';
import {useTaxInvoiceTableFilterActions} from '../../utils';
import {useDebounceEffect} from '../../../../utils/react';
import {dateToUTCString} from '../../../../utils/date';
import {GetApp} from '@material-ui/icons';
import {EXPORT_TAX_INVOICE_DOCUMENTS_URL} from '../../promises';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    padding: '.5rem',

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  pickerLabel: {
    width: 'auto',
    fontSize: '.875rem',
    whiteSpace: 'nowrap',
  },
  searchInput: {
    width: 'auto',

    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  iconButton: {
    background: `${colors.primary}29`,
  },
  modalPicker: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '320px',
  },
  clearButton: {
    background: `${colors.danger}15`,
    color: colors.danger,
    '&:hover': {
      background: `${colors.danger}25`,
    },
  },
}));

export default function TaxInvoiceFilters() {
  const classes = useStyles();

  const {
    setQueryFilter,
    setEndDateFilter,
    setStartDateFilter,
    resetTableFilters,
  } = useTaxInvoiceTableFilterActions();

  const [pickerModal, setPickerModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [searchValue, setSearchValue] = useState('');

  const hasActiveFilters =
    searchValue !== '' || startDate !== null || endDate !== null;

  const handleClearFilters = () => {
    setSearchValue('');
    setStartDate(null);
    setEndDate(null);
    resetTableFilters();
  };

  const firstRender = useRef(true);
  useDebounceEffect(
    () => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      setQueryFilter(searchValue);
    },
    750,
    [searchValue]
  );

  const handleChangeStartDate = date => {
    setStartDate(date);
    if (date) {
      const utcDate = dateToUTCString(new Date(date), {startDay: true});
      setStartDateFilter(utcDate);
    } else {
      setStartDateFilter('');
    }
  };

  const handleChangeEndDate = date => {
    setEndDate(date);
    if (date) {
      const utcDate = dateToUTCString(new Date(date), {endDay: true});
      setEndDateFilter(utcDate);
    } else {
      setEndDateFilter('');
    }
  };

  const exportUrl = useMemo(() => {
    const baseUrl = EXPORT_TAX_INVOICE_DOCUMENTS_URL;

    if (!startDate || !endDate) return;
    const queryParams = new URLSearchParams();
    const utcStartDate = dateToUTCString(new Date(startDate), {startDay: true});
    const utcEndDate = dateToUTCString(new Date(endDate), {endDay: true});

    queryParams.append('init_date', utcStartDate);
    queryParams.append('end_date', utcEndDate);

    const origin = window.location.origin;

    return `${origin}${baseUrl}?${queryParams.toString()}`;
  }, [endDate, startDate]);

  return (
    <div className={classes.root}>
      <SearchInput
        placeholder="Buscar cliente"
        className={classes.searchInput}
        value={searchValue}
        onChange={setSearchValue}
      />

      {hasActiveFilters && (
        <TiendadaTooltip title="Limpiar filtros" placement="top">
          <IconButton
            className={classes.clearButton}
            onClick={handleClearFilters}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </TiendadaTooltip>
      )}

      <Hidden mdDown>
        <Typography className={classes.pickerLabel}>
          Filtrar por fecha
        </Typography>
      </Hidden>

      <Hidden xsDown>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <DatePicker
              label="Desde"
              handleChange={handleChangeStartDate}
              value={startDate}
              InputProps={{disabled: true}}
            />
          </Grid>
          <Grid item xs={5}>
            <DatePicker
              disabled={!startDate}
              label="Hasta"
              handleChange={handleChangeEndDate}
              value={endDate}
              minDate={startDate}
              InputProps={{disabled: true}}
              minDateMessage={
                'La fecha debe ser posterior a la fecha de inicio'
              }
            />
          </Grid>
          <Grid item xs={1}>
            <TiendadaTooltip
              title={
                exportUrl
                  ? 'Exportar facturas'
                  : 'Selecciona un rango de fechas para exportar'
              }
              placement="top"
            >
              <div>
                <IconButton
                  color="primary"
                  className={classes.iconButton}
                  disabled={!exportUrl}
                  onClick={() => window.open(exportUrl)}
                >
                  <GetApp />
                </IconButton>
              </div>
            </TiendadaTooltip>
          </Grid>
        </Grid>
      </Hidden>

      <Hidden smUp>
        <IconButton
          className={classes.iconButton}
          color="primary"
          onClick={() => setPickerModal(true)}
        >
          <CalendarIcon />
        </IconButton>

        <TiendadaTooltip
          title={
            exportUrl
              ? 'Exportar facturas'
              : 'Selecciona un rango de fechas para exportar'
          }
          placement="top"
        >
          <div>
            <IconButton
              color="primary"
              className={classes.iconButton}
              disabled={!exportUrl}
              onClick={() => window.open(exportUrl)}
            >
              <GetApp />
            </IconButton>
          </div>
        </TiendadaTooltip>

        <CustomModal
          open={pickerModal}
          handleCloseModal={() => setPickerModal(false)}
        >
          <div className={classes.modalPicker}>
            <Typography variant="h6">Filtrar por fecha de emisión</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DatePicker
                  label="Desde"
                  handleChange={handleChangeStartDate}
                  value={startDate}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  disabled={!startDate}
                  label="Hasta"
                  handleChange={handleChangeEndDate}
                  value={endDate}
                  minDate={startDate}
                  minDateMessage={
                    'La fecha debe ser posterior a la fecha de inicio'
                  }
                />
              </Grid>
            </Grid>
          </div>
        </CustomModal>
      </Hidden>
    </div>
  );
}
